import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { numOrNull } from '../../utils/buildPoolEngineInput';
import { describeShareEquivalent } from '../../utils/poolShare';
import {
  POOL_STRATEGY_OPTIONS, changesShareBase, readsShare, restateShare, shareLabelFor,
} from '../../utils/poolShareStrategy';

// O-4756. The active member's pool-share control for the SIMPLE editor — the
// sibling of V3PoolMemberShare in the complex one. It lives in its own file
// because it is the only stateful section on the simple deal form (a keystroke
// draft plus an identity effect); the rest are pure presentational blocks, and
// burying a hook pair among them hides it from anyone scanning for state.
// Behaviour is deliberately identical to the complex control — the two editors
// write the same two settlement columns, so a rule that holds in one and not
// the other is a bug by construction.

// The offer columns this card owns while the pool is active. The deal-type card
// writes the same two from a snapshot taken at mount, so a save from there would
// revert whatever was set here.
export const POOL_MEMBER_DEAL_FIELDS = ['dollar_amount', 'dollar_or_percentage_operator'];

const OR = 'OR';
const AND = 'AND';

const SimplePoolShareSection = ({ offer, earnings, onChangeField }) => {
  const strategy = offer?.pool_payout_strategy || 'explicit';
  // Parity with the complex editor: the member's own guarantee drawn FROM the
  // pool, and whether it adds to their share or competes with it.
  const operator = offer?.dollar_or_percentage_operator || AND;
  // Raw keystrokes, so the field can be momentarily empty without persisting an
  // empty. Clearing the seeded share to type a real one is the normal gesture,
  // and a blank is rejected for this strategy — which would roll the strategy
  // back too, undoing the selection just made.
  const [shareDraft, setShareDraft] = useState(null);

  // The keystroke draft must not outlive the record it was typed against —
  // switching artist would otherwise show the previous member's digits over the
  // new one's stored share.
  useEffect(() => { setShareDraft(null); }, [offer?.id]);

  const onChangeShare = (raw) => {
    setShareDraft(raw);
    const next = numOrNull(raw);
    if (next === null && strategy === 'percentage_of_remainder') return;
    onChangeField('pool_percentage', next);
  };

  const showPercentage = readsShare(strategy);
  const percentageLabel = shareLabelFor(strategy);
  const shareEquivalent = describeShareEquivalent(strategy, offer?.pool_percentage, earnings);
  const shareEquivalentId = `simple-pool-member-share-equivalent-${offer?.id}`;

  const onChangeStrategy = (next) => {
    if (next === strategy) return;

    onChangeField('pool_payout_strategy', next);
    setShareDraft(null);

    if (!changesShareBase(strategy, next)) return;
    onChangeField('pool_percentage', restateShare({
      offerId: offer?.id, from: strategy, to: next, share: offer?.pool_percentage ?? null,
    }));
  };

  return (
    <section className="simple-offer__section simple-pool-member-share" data-testid="simple-pool-member-share">
      <h3 className="simple-offer__section-title">POOL SHARE</h3>
      <div className="simple-mod__grid simple-mod__grid--3">
        <label className="simple-mod__field simple-mod__field--full">
          <span className="simple-mod__label">Payout strategy</span>
          <select
            className="simple-mod__select"
            data-testid="simple-pool-member-strategy"
            value={strategy}
            onChange={(e) => onChangeStrategy(e.target.value)}
          >
            {POOL_STRATEGY_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
          </select>
        </label>
        <label className="simple-mod__field">
          <span className="simple-mod__label">Guarantee</span>
          <span className="simple-mod__input-wrap">
            <span className="simple-mod__affix simple-mod__affix--prefix">$</span>
            <input
              type="number" step="0.01"
              className="simple-mod__input simple-mod__input--prefix"
              data-testid="simple-pool-member-guarantee"
              value={offer?.dollar_amount ?? ''}
              onChange={(e) => onChangeField('dollar_amount', numOrNull(e.target.value))}
            />
          </span>
        </label>

        {showPercentage && (
          <div className="simple-mod__field">
            <span className="simple-mod__label">Combine</span>
            <div className="deal-terms-form__operator-group" role="radiogroup" aria-label="Guarantee combiner">
              <button
                type="button"
                className={`deal-terms-form__operator-btn ${operator === AND ? 'deal-terms-form__operator-btn--active' : ''}`}
                data-testid="simple-pool-member-operator-and"
                onClick={() => onChangeField('dollar_or_percentage_operator', AND)}
                aria-pressed={operator === AND}
                aria-label="Guarantee plus share"
              >
                +
              </button>
              <button
                type="button"
                className={`deal-terms-form__operator-btn ${operator === OR ? 'deal-terms-form__operator-btn--active' : ''}`}
                data-testid="simple-pool-member-operator-or"
                onClick={() => onChangeField('dollar_or_percentage_operator', OR)}
                aria-pressed={operator === OR}
                aria-label="Guarantee versus share"
              >
                vs
              </button>
            </div>
          </div>
        )}

        {showPercentage && (
          <label className="simple-mod__field">
            <span className="simple-mod__label">{percentageLabel}</span>
            <span className="simple-mod__input-wrap">
              <input
                type="number" step="0.01"
                className="simple-mod__input simple-mod__input--suffix"
                data-testid="simple-pool-member-percentage"
                aria-label={percentageLabel}
                aria-describedby={shareEquivalent ? shareEquivalentId : undefined}
                value={shareDraft !== null ? shareDraft : (offer?.pool_percentage ?? '')}
                onChange={(e) => onChangeShare(e.target.value)}
                onBlur={() => setShareDraft(null)}
              />
              <span className="simple-mod__affix simple-mod__affix--suffix"><i className="fas fa-percent" aria-hidden="true" /></span>
            </span>
            {shareEquivalent && (
              <span
                id={shareEquivalentId}
                className="simple-pool-member-share__equivalent"
                data-testid="simple-pool-member-share-equivalent"
              >
                {shareEquivalent}
              </span>
            )}
          </label>
        )}
        {/* O-4755. Cap/floor clamp this member's take whatever the strategy —
          * the engine applies them to a remainder line the same as an explicit
          * one — so unlike the share above they show for every strategy. */}
        <label className="simple-mod__field">
          <span className="simple-mod__label">Payout cap</span>
          <span className="simple-mod__input-wrap">
            <span className="simple-mod__affix simple-mod__affix--prefix">$</span>
            <input
              type="number" step="0.01" min="0"
              className="simple-mod__input simple-mod__input--prefix"
              data-testid="simple-pool-member-cap"
              value={offer?.artist_payout_cap ?? ''}
              onChange={(e) => onChangeField('artist_payout_cap', numOrNull(e.target.value))}
            />
          </span>
        </label>
        <label className="simple-mod__field">
          <span className="simple-mod__label">Payout floor</span>
          <span className="simple-mod__input-wrap">
            <span className="simple-mod__affix simple-mod__affix--prefix">$</span>
            <input
              type="number" step="0.01" min="0"
              className="simple-mod__input simple-mod__input--prefix"
              data-testid="simple-pool-member-floor"
              value={offer?.artist_payout_floor ?? ''}
              onChange={(e) => onChangeField('artist_payout_floor', numOrNull(e.target.value))}
            />
          </span>
        </label>
      </div>
    </section>
  );
};

SimplePoolShareSection.propTypes = {
  offer: PropTypes.object.isRequired,
  earnings: PropTypes.object,
  onChangeField: PropTypes.func.isRequired,
};

SimplePoolShareSection.defaultProps = {
  earnings: null,
};

export default SimplePoolShareSection;
